import React, { forwardRef } from "react";
import { cn } from "../../styles";
import { themeTextStyles } from "../../styles";
import { LocalProp, ThemeColor } from "../../props";
import { FontIcon } from "./FontIcon";
import { IconBaseProps } from "./types";

export * from "./FontIcon";
export * from "./types";

export interface IconProps
  extends IconBaseProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  /**
   * SVG path string.
   * Just one of `local`, `name` or `path` should be provided.
   */
  path?: string;

  /**
   * Material Design Icon name (without 'mdi-' prefix).
   * Just one of `local`, `name` or `path` should be provided.
   */
  name?: string;

  /**
   * Localization switch that resolves to a string that is the
   * Material Design Icon name (without 'mdi-' prefix).
   * Just one of `local`, `name` or `path` should be provided.
   */
  local?: LocalProp;
}

/**
 * Flexible Icon component that supports both Material Design Icons (font-based) and custom SVG icons.
 * Automatically renders as either a span element with MDI classes or an SVG element based on props provided.
 * Supports theme colors and customizable sizing.
 * Forwards refs and additional props to the rendered element.
 *
 * SVG icons should be preferred when the icon is known at development time,
 * as they provide better quality rendering and avoid loading the large icon font.
 */

export const Icon = forwardRef<SVGSVGElement | HTMLSpanElement, IconProps>(
  ({ path, name, local, ...props }, ref) => {
    if (name || local)
      return (
        <FontIcon
          ref={ref as React.Ref<HTMLSpanElement>}
          name={name}
          local={local}
          {...props}
        />
      );
    if (!path) return null;
    return (
      <SvgIcon
        ref={ref as React.Ref<SVGSVGElement>}
        path={path}
        {...(props as any)}
      />
    );
  }
);
Icon.displayName = "Icon";

export interface SvgIconProps
  extends IconBaseProps,
    Omit<React.SVGAttributes<SVGSVGElement>, "color"> {
  path: string;
}

export const SvgIcon = forwardRef<SVGSVGElement, SvgIconProps>(
  (
    {
      className = "",
      style,
      path,
      color = ThemeColor.none,
      size = 20,
      muted = false,
      ...props
    },
    ref
  ) => {
    return (
      <svg
        ref={ref}
        className={cn(
          themeTextStyles[color],
          muted && "opacity-50",
          "inline-block shrink-0",
          className
        )}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        {...props}
      >
        <path d={path} fill="currentColor" />
      </svg>
    );
  }
);
SvgIcon.displayName = "SvgIcon";
